/**
 * Regras de promoção/rebaixamento por liga (Sprint F §3.1) — espelho cliente.
 *
 * Mantém-se SINCRONIZADO com `functions/src/shared/leagueRules.ts`. As regras
 * são lógica pura — qualquer mudança aqui deve refletir no servidor (e vice-versa).
 *
 * Uso no cliente:
 *  - `getRankZone(rank, totalMembers, tier)` para colorir UI ("você está em zona
 *    de promoção / segura / rebaixamento") considerando o tier.
 *  - Previsões na PreviewBar antes do close semanal.
 */

import type { LeagueTier, RankingMetric } from "../../../services/firebase/leagueService";

export const TIER_ORDER: readonly LeagueTier[] = ["bronze", "silver", "gold", "diamond"] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Tetos diários da Liga — espelho de `functions/src/shared/constants.ts`
// ─────────────────────────────────────────────────────────────────────────────
//
// Existem aqui por um motivo específico: até 31/07 a UI da Liga dizia que havia
// "um limite diário" e **não dizia qual**. Usuários do beta leram 200+ páginas,
// viram "só contou 200" e leram isso como roubo — que é o desfecho normal de um
// teto que existe e não é comunicado.
//
// Hard-coded e travados por teste (`leagueRules.test.ts`) contra as constantes
// do servidor. É o mesmo padrão do espelho de economia do Foco
// (`features/focus/lib/focusEconomy.ts`): um número que a UI promete e o
// servidor cumpre não pode divergir em silêncio.
//
// ⚠️ Mudar um valor aqui NÃO muda o comportamento — só a promessa. A fonte é o
// servidor; se os dois divergirem, o teste quebra e a UI é que está mentindo.

/**
 * Quantos leitores cabem numa sala. Entrou no espelho quando o popup de
 * boas-vindas passou a dizer o número ("você disputa com até 30 leitores") —
 * mesma regra dos tetos: número que a tela promete vira espelho travado.
 */
export const LEAGUE_ROOM_MAX_MEMBERS = 30;
/**
 * Quantos leitores cabem numa sala PRIVADA. Constante separada da pública desde
 * 19/08: lá o número é economia (promoção/rebaixamento são percentual dele),
 * aqui é engenharia — `entries` é um mapa num único doc, e doc do Firestore
 * para em 1 MiB (~2.673 membros, medido a 392 B por entry com foto).
 *
 * ⚠️ Este número aparece na tela ao criar e ao entrar numa liga. Antes de 19/08
 * não aparecia em lugar nenhum: quem criava só descobria o teto quando o
 * convidado seguinte tomava erro.
 */
export const PRIVATE_LEAGUE_MAX_MEMBERS = 200;
/**
 * Clube do livro (liga privada "com leitura"): quantos livros cada membra põe
 * no pote. A tela do pote diz o número ("4 de 10") — espelho de
 * `CLUB_POT_PER_MEMBER` (`docs/systems/league-club-book.md` §7.7; 3 → 10 em 13/10, C6).
 */
export const CLUB_POT_PER_MEMBER = 10;
/**
 * Clube do livro: prazos-alvo do livro da vez, em dias (o 1º é o default ao
 * ligar o modo leitura). Espelho de `CLUB_CYCLE_DAYS`; é meta exibida, não trava.
 */
export const CLUB_CYCLE_DAYS = [30, 15] as const;
/**
 * ── Rebalanceamento 27/08 (espelho — a decisão mora em constants.ts do server):
 * a Liga mede LEITURA numa régua só, e o Foco vale um prêmio de credibilidade
 * de 1,2× (6/min contra 5/pág; ppm mediano da base = 1,0). O XP de páginas
 * virou soft cap em degraus: taxa cheia até a 100ª página do dia, reduzida até
 * a 200ª, zero além — nada disso se escreve à mão em tela nenhuma.
 */
/** XP por página marcada manualmente (taxa CHEIA — até a 100ª página do dia). */
export const PAGE_XP_RATE = 5;
/** Páginas do dia que valem a taxa cheia; da seguinte em diante vale a reduzida. */
export const PAGE_XP_SOFT_CAP_PAGES = 100;
/** XP por página no degrau reduzido (da 101ª à 200ª do dia). */
export const PAGE_XP_REDUCED_RATE = 2;
/** Além desta página do dia, XP zera (a leitura segue na ofensiva/estatísticas). */
export const PAGE_XP_HARD_CAP_PAGES = 200;
/** Teto diário (dia BR) de XP manual — DERIVADO da régua (700 hoje). */
export const MAX_DAILY_PAGE_XP =
  PAGE_XP_RATE * PAGE_XP_SOFT_CAP_PAGES +
  PAGE_XP_REDUCED_RATE * (PAGE_XP_HARD_CAP_PAGES - PAGE_XP_SOFT_CAP_PAGES);
/** Teto diário (dia BR) de páginas DECLARADAS que contam na métrica `pages`. */
export const MAX_DAILY_LEAGUE_PAGES_FROM_MANUAL = 100;
/** XP por minuto de sessão de Foco (1,2× a página — contrato, ver o espelho). */
export const FOCUS_XP_PER_MIN = 6;
/**
 * Piso de ritmo do XP pendente do Foco (BUG-310, decisão 30/09): cada página
 * registrada libera até `1 / piso` minutos de XP (0,1 ⇒ 10 min). Espelho de
 * `functions/src/shared/constants.ts`; paridade em `focusXpPending.test.ts`.
 */
export const FOCUS_XP_MIN_PAGES_PER_MIN = 0.1;
/** Teto diário (dia BR) de minutos de Foco elegíveis a XP de liga. */
export const MAX_DAILY_FOCUS_REWARD_MIN = 150;

export interface LeaguePromotionRule {
  promotePercent: number;
  relegatePercent: number;
  minPromote: number;
  minRelegate: number;
}

export const LEAGUE_PROMOTION_RULES: Record<LeagueTier, LeaguePromotionRule> = {
  bronze:  { promotePercent: 0.50, relegatePercent: 0.10, minPromote: 5, minRelegate: 3 },
  silver:  { promotePercent: 0.30, relegatePercent: 0.20, minPromote: 4, minRelegate: 4 },
  gold:    { promotePercent: 0.15, relegatePercent: 0.25, minPromote: 2, minRelegate: 5 },
  diamond: { promotePercent: 0.00, relegatePercent: 0.30, minPromote: 0, minRelegate: 6 },
};

export interface MovementCounts {
  promote: number;
  relegate: number;
}

export function computeMovementCounts(
  tier: LeagueTier,
  memberCount: number,
): MovementCounts {
  if (memberCount <= 1) return { promote: 0, relegate: 0 };
  if (memberCount === 2) {
    return { promote: tier === "diamond" ? 0 : 1, relegate: 0 };
  }
  if (memberCount === 3) {
    return {
      promote: tier === "diamond" ? 0 : 1,
      relegate: 1,
    };
  }

  const rule = LEAGUE_PROMOTION_RULES[tier];

  let promote = Math.max(
    Math.min(rule.minPromote, memberCount),
    Math.ceil(memberCount * rule.promotePercent),
  );
  let relegate = Math.max(
    Math.min(rule.minRelegate, memberCount),
    Math.ceil(memberCount * rule.relegatePercent),
  );

  if (tier === "diamond") promote = 0;

  const cap = memberCount - 2;
  if (promote + relegate > cap) {
    const excess = promote + relegate - cap;
    const trimRelegate = Math.min(relegate, excess);
    relegate -= trimRelegate;
    const remaining = excess - trimRelegate;
    if (remaining > 0) {
      promote = Math.max(0, promote - remaining);
    }
  }

  return { promote, relegate };
}

export type RankZone = "promote" | "safe" | "relegate";

// ── Recompensas por colocação (espelho de PLACEMENT_REWARDS_BY_TIER do server) ─

export interface PlacementReward {
  coins: number;
  /** ID do badge — opcional. */
  badge?: string;
}

/**
 * Espelho EXATO de `functions/src/shared/leagueRules.ts` — mudou lá, espelhe
 * aqui (regra #3 da skill profile-and-gamification). Usado pra UI do "ganho
 * legível": o prêmio de cada posição aparece na tela da liga (A2 — sem isso a
 * liga mostrava posição e XP, mas não o que se ganha).
 */
export const PLACEMENT_REWARDS_BY_TIER: Record<LeagueTier, readonly PlacementReward[]> = {
  bronze: [
    { coins: 50, badge: "liga_bronze_topo" },
    { coins: 25, badge: "liga_bronze_topo" },
    { coins: 25, badge: "liga_bronze_topo" },
    { coins: 10 }, { coins: 10 }, { coins: 10 }, { coins: 10 },
    { coins: 10 }, { coins: 10 }, { coins: 10 },
  ],
  silver: [
    { coins: 100, badge: "liga_silver_topo" },
    { coins: 50,  badge: "liga_silver_topo" },
    { coins: 50,  badge: "liga_silver_topo" },
    { coins: 25 }, { coins: 25 }, { coins: 25 }, { coins: 25 },
    { coins: 25 }, { coins: 25 }, { coins: 25 },
    { coins: 10 }, { coins: 10 }, { coins: 10 }, { coins: 10 }, { coins: 10 },
    { coins: 10 }, { coins: 10 }, { coins: 10 }, { coins: 10 }, { coins: 10 },
  ],
  gold: [
    { coins: 200, badge: "liga_gold_topo" },
    { coins: 100, badge: "liga_gold_topo" },
    { coins: 100, badge: "liga_gold_topo" },
    { coins: 50 }, { coins: 50 }, { coins: 50 }, { coins: 50 },
    { coins: 50 }, { coins: 50 }, { coins: 50 },
    { coins: 20 }, { coins: 20 }, { coins: 20 }, { coins: 20 }, { coins: 20 },
    { coins: 20 }, { coins: 20 }, { coins: 20 }, { coins: 20 }, { coins: 20 },
  ],
  diamond: [
    { coins: 500, badge: "imperador_diamante" },
    { coins: 250, badge: "liga_diamond_podio" },
    { coins: 250, badge: "liga_diamond_podio" },
    { coins: 100 }, { coins: 100 }, { coins: 100 }, { coins: 100 },
    { coins: 100 }, { coins: 100 }, { coins: 100 },
    { coins: 50 }, { coins: 50 }, { coins: 50 }, { coins: 50 }, { coins: 50 },
    { coins: 50 }, { coins: 50 }, { coins: 50 }, { coins: 50 }, { coins: 50 },
  ],
};

/** Recompensa da posição (1-based) no tier, ou null se fora da premiação. */
export function getPlacementRewardForRank(
  tier: LeagueTier,
  rank: number,
): PlacementReward | null {
  return PLACEMENT_REWARDS_BY_TIER[tier][rank - 1] ?? null;
}

// ── Liga pequena: espelho da regra de promoção por PONTUAÇÃO ─────────────────
//
// Espelho EXATO de `functions/src/shared/leagueRules.ts` (§8 de
// `docs/systems/economia-liga-limites.md`), travado por
// `tests/functions/leagueZoneParity.test.ts` — que roda as DUAS implementações
// sobre a mesma matriz de entradas e compara o veredito, não só as constantes.
//
// Não há import cross-boundary (`functions/tsconfig.json` tem `rootDir: "src"`),
// então espelho + paridade é o padrão da casa — mesmo de `CREATOR_TAG_NETWORKS`
// e dos tetos diários logo acima.
//
// ⚠️ Mudou lá, muda aqui, e o teste é quem cobra. Divergir aqui é a UI
// prometendo promoção que a virada não entrega — o defeito que o invariante 3
// da skill `profile-and-gamification` existe para impedir.

/** A partir deste tamanho, a sala volta a promover/rebaixar por POSIÇÃO. */
export const SMALL_LEAGUE_MIN_MEMBERS = 5;

/**
 * Piso semanal de XP que promove em sala pequena. `null` = o tier não promove.
 * Reescalado ×3 em 27/08 junto do rebalanceamento do XP (o piso é o único
 * limiar denominado em XP — parado, a promoção viraria trivial com as taxas
 * novas). Calibração no docblock do servidor.
 */
export const SMALL_LEAGUE_PROMOTION_FLOOR: Record<LeagueTier, number | null> = {
  bronze: 450,
  silver: 1200,
  gold: 2400,
  diamond: null,
};

/** Sala vazia não é sala pequena — não há ninguém para promover. */
export function isSmallLeagueRoom(memberCount: number): boolean {
  return memberCount > 0 && memberCount < SMALL_LEAGUE_MIN_MEMBERS;
}

/** A sala decide por PISO (`true`) ou por POSIÇÃO (`false`). */
export function usesScoreRegime(
  memberCount: number,
  metric: RankingMetric = "xp",
): boolean {
  return isSmallLeagueRoom(memberCount) && metric === "xp";
}

/**
 * Quanto falta para GARANTIR a subida, ou `null` se a pergunta não se aplica
 * (sala grande, métrica não-XP, Diamante, ou já garantido). Em sala grande não
 * existe número que garanta — depende de quanto os outros leram.
 */
export function xpToPromotion(
  tier: LeagueTier,
  score: number,
  memberCount: number,
  metric: RankingMetric = "xp",
): number | null {
  if (!usesScoreRegime(memberCount, metric)) return null;
  const floor = SMALL_LEAGUE_PROMOTION_FLOOR[tier];
  if (floor === null) return null;
  const missing = floor - score;
  return missing > 0 ? missing : null;
}

/**
 * Movimentação REAL da sala concreta — o que `computeMovementCounts` é para o
 * tamanho, esta é para a sala com os scores dentro.
 *
 * Em regime de piso os promovidos são um **prefixo contíguo** do ranking (ele é
 * ordenado desc), e é isso que deixa a faixa da UI continuar sendo um `slice` e
 * concordar com a cor de cada linha por construção.
 *
 * `sortedScores` precisa vir em ordem DECRESCENTE.
 */
export function resolveRoomMovement(
  tier: LeagueTier,
  sortedScores: readonly number[],
  metric: RankingMetric = "xp",
): MovementCounts {
  if (usesScoreRegime(sortedScores.length, metric)) {
    const floor = SMALL_LEAGUE_PROMOTION_FLOOR[tier];
    const promote =
      floor === null ? 0 : sortedScores.filter((score) => score >= floor).length;
    return { promote, relegate: 0 };
  }
  return computeMovementCounts(tier, sortedScores.length);
}

/**
 * Em que zona a pessoa está. Substitui o antigo `getRankZoneByTier`, que só
 * recebia o rank: em sala pequena a posição não decide nada, então uma
 * assinatura sem `score` deixava o defeito exprimível — o 1º de uma sala de 2
 * era pintado como "vai subir" mesmo abaixo do piso, e quando os dois batiam o
 * piso só um era pintado.
 */
export function resolveRankZone(
  tier: LeagueTier,
  rank: number,
  score: number,
  totalMembers: number,
  metric: RankingMetric = "xp",
): RankZone {
  if (usesScoreRegime(totalMembers, metric)) {
    const floor = SMALL_LEAGUE_PROMOTION_FLOOR[tier];
    // Sala pequena não rebaixa: só existe "sobe" ou "fica".
    return floor !== null && score >= floor ? "promote" : "safe";
  }

  const { promote, relegate } = computeMovementCounts(tier, totalMembers);
  if (promote > 0 && rank <= promote) return "promote";
  if (relegate > 0 && rank > totalMembers - relegate) return "relegate";
  return "safe";
}

/**
 * O nome curto da divisão — "Ouro", não "Liga de Ouro".
 *
 * Existe separado de `LEAGUE_LABELS` (que vive no `leagueService`) por uma
 * razão de GRAFO, não de estilo: aquele módulo importa `db` do Firebase, e
 * este aqui é lógica pura, testada em `@vitest-environment node`. Um import de
 * valor arrastaria o SDK inteiro para dentro da suíte de regras.
 *
 * A frase que os consome é "top 5 sobe pro **Ouro**", onde a forma longa
 * ("sobe pro Liga de Ouro") é agramatical. A paridade com `LEAGUE_LABELS` é
 * travada em `tests/components/leagueCardHome.test.tsx`, que já carrega os
 * dois lados.
 */
export const LEAGUE_SHORT_LABELS: Record<LeagueTier, string> = {
  bronze: "Bronze",
  silver: "Prata",
  gold: "Ouro",
  diamond: "Diamante",
};

export function nextLeagueUp(tier: LeagueTier): LeagueTier {
  const idx = TIER_ORDER.indexOf(tier);
  return idx >= 0 && idx < TIER_ORDER.length - 1 ? TIER_ORDER[idx + 1] : tier;
}

export function nextLeagueDown(tier: LeagueTier): LeagueTier {
  const idx = TIER_ORDER.indexOf(tier);
  return idx > 0 ? TIER_ORDER[idx - 1] : tier;
}

export function isTierUp(prev: LeagueTier, next: LeagueTier): boolean {
  return TIER_ORDER.indexOf(next) > TIER_ORDER.indexOf(prev);
}

export function isTierDown(prev: LeagueTier, next: LeagueTier): boolean {
  return TIER_ORDER.indexOf(next) < TIER_ORDER.indexOf(prev);
}
